import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Actions, Store } from '@ngxs/store';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { Observable } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ScheduledStreamContentDataSource } from './datasource';
import { MaxBrainScheduledStreamContent } from 'app/library/scheduled-stream-content/models/scheduled-stream-content.model';
import { ScheduledStreamContentsState } from 'app/library/scheduled-stream-content/models/scheduled-stream-contents.state';
import { ScheduledStreamContentService } from 'app/library/scheduled-stream-content/services/scheduled-stream-content.service';
import { EngageScheduleFeaturedContentDialogComponent } from '../../dialogs/engage-schedule-featured-content-dialog/engage-schedule-featured-content-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import * as moment from 'moment';

@Component({
    selector: 'app-scheduled-stream-content-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ScheduledStreamContentListComponent extends UnsubscribeOnDestroy implements OnInit, AfterViewInit {
    @Input()
    filteredScheduledStreamContent$: Observable<MaxBrainScheduledStreamContent[]>;

    @Input()
    scheduledStreamContentStateSearchTerm$: Observable<string>;
    scheduledStreamContentStateSearchTerm: string;

    @Input()
    scheduledStreamContentStateTotalCount$: Observable<number>;

    @Input()
    loadScheduledStreamContent$: Observable<boolean>;

    @Input()
    audienceId: string;

    @Input()
    featuredStreamContentId: string;

    @Input()
    selectedPostingTypeId: string;

    dataSource: ScheduledStreamContentDataSource;
    displayedColumns = ['avatar', 'name', 'type', 'startDate', 'endDate', 'buttons'];

    @ViewChild(MatPaginator)
    paginator: MatPaginator;
    @ViewChild(MatSort)
    sort: MatSort;
    @ViewChild('chipList')
    chipList: ElementRef;

    engageScheduleFeaturedContentDialogRef: MatDialogRef<EngageScheduleFeaturedContentDialogComponent>;

    constructor(private _store: Store, private _action$: Actions, private _matDialog: MatDialog, private _featuredStreamContentService: ScheduledStreamContentService) {
        super();
    }

    ngOnInit(): void {
        this.dataSource = new ScheduledStreamContentDataSource(this.filteredScheduledStreamContent$, this._store, this._action$);
    }

    ngAfterViewInit(): void {
        this.scheduledStreamContentStateSearchTerm$
            .pipe(
                filter((searchTerm, index) => {
                    this.scheduledStreamContentStateSearchTerm = searchTerm || '';
                    return index > 0;
                }),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                this.loadScheduledStreamContentPage(true);
            });

        this.paginator.page.subscribe(({ length, pageIndex, pageSize }: PageEvent) => {
            const itemsleft = length - pageIndex * pageSize;
            const itemsInThisPage = itemsleft < pageSize ? itemsleft : pageSize;
            // FIXME: these hardcoded numbers represent the height of the rest of the elements surrounding the table within the layout content
            const tableHeight = 80 + 24 + 64 + 56 + itemsInThisPage * 65 + 72 + 56;
            const contentElementHeight = document.getElementById('container-2').offsetHeight;
            const scrollTop = tableHeight > contentElementHeight ? tableHeight - contentElementHeight : 0;

            setTimeout(() => {
                document.getElementById('container-3').scrollTop = scrollTop;
            });
        });

        this.loadScheduledStreamContentPage(false);

        this.sort.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sort) => {
            this.loadScheduledStreamContentPage(true);
        });

        this.paginator.page.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
            this.loadScheduledStreamContentPage(false);
        });

        this.loadScheduledStreamContent$
            .pipe(
                filter((resetPageIndex) => resetPageIndex !== null),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((resetPageIndex) => {
                this.loadScheduledStreamContentPage(resetPageIndex);
            });
    }

    openScheduleFeaturedContentDialog(scss: MaxBrainScheduledStreamContent): void {
        this.engageScheduleFeaturedContentDialogRef = this._matDialog.open(EngageScheduleFeaturedContentDialogComponent, {
            disableClose: false,
            width: '648px',
            autoFocus: false,
        });
        // if validFrom is in the past, we set today's date as the date that is valid
        const minDate = moment().isAfter(scss.posting.validFrom) ? moment().toString() : scss.posting.validFrom;
        this.engageScheduleFeaturedContentDialogRef.componentInstance.postId = scss.id;
        this.engageScheduleFeaturedContentDialogRef.componentInstance.postName = scss.posting.translations[0].title;
        this.engageScheduleFeaturedContentDialogRef.componentInstance.minDate = new Date(minDate);
        this.engageScheduleFeaturedContentDialogRef.componentInstance.maxDate = scss.posting.validTo ? new Date(scss.posting.validTo) : null;
        this.engageScheduleFeaturedContentDialogRef.componentInstance.scss = scss;

        this.engageScheduleFeaturedContentDialogRef
            .afterClosed()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(async (result) => {
                if (result && result.data) {
                    const data: MaxBrainScheduledStreamContent = {
                        id: scss.id,
                        audience: { id: this.audienceId },
                        posting: { id: scss.posting.id },
                        featuredStream: { id: this.featuredStreamContentId },
                        startDate: result.data.startDate || null,
                        endDate: result.data.endDate || null,
                        priority: result.data.priority || null,
                    } as MaxBrainScheduledStreamContent;

                    await this._featuredStreamContentService.updateScheduledStreamContent(data);
                    this.loadScheduledStreamContentPage(true);
                }

                this.engageScheduleFeaturedContentDialogRef = null;
            });
    }

    loadScheduledStreamContentPage(resetPageIndex = false): void {
        if (resetPageIndex) {
            this.paginator.pageIndex = 0;
        }

        const searchTerm = this._store.selectSnapshot(ScheduledStreamContentsState.getSearchTerm) || this.scheduledStreamContentStateSearchTerm || '';

        this.dataSource.loadScheduledStreamContent(
            this.audienceId,
            this.featuredStreamContentId,
            this.paginator.pageSize,
            this.paginator.pageIndex + 1,
            this.sort.active,
            this.sort.direction,
            searchTerm,
            this.selectedPostingTypeId
        );
    }

    async onDeleteScss(scheduledStreamContent: MaxBrainScheduledStreamContent): Promise<void> {
        await this._featuredStreamContentService.deleteScheduledStreamContent(scheduledStreamContent.id);

        this.loadScheduledStreamContentPage(true);
    }

    visualLength(text: string): number {
        const ruler = document.getElementById('ruler');
        ruler.innerHTML = text;
        const toReturn = ruler.offsetWidth;
        ruler.innerHTML = '';
        return toReturn;
    }
}
